import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { BackLink } from '../components/ui/BackLink';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { getAuthor } from '../content/authors';
import { tutorialsByAuthor } from '../content/tutorials';
import { authorJsonLd, authorUrl } from '../content/tutorialJsonLd';
import { AuthorAvatar } from '../components/tutorials/AuthorByline';

/**
 * An AI-Writer profile — /authors/{slug}. Crawlable (no auth): the writer's backstory, what they
 * cover, and the tutorials they wrote, with Person/ProfilePage structured data (author E-E-A-T).
 */
const AuthorPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const author = getAuthor(slug);
  const tutorials = author ? tutorialsByAuthor(author.id) : [];

  useDocumentMeta({
    title: author ? `${author.name} — ${author.tagline} — Wisetree AI` : 'Author — Wisetree AI',
    description: author?.bio,
    canonical: author ? authorUrl(author.slug) : 'https://wisetree.ai/tutorials',
    jsonLd: author ? authorJsonLd(author, tutorials.map((t) => ({ slug: t.slug, title: t.title }))) : undefined,
  });

  if (!author) {
    return (
      <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <BackLink to="/tutorials" label="Tutorials" />
          <div className="mt-8">
            <h1 className="text-2xl font-bold">Writer not found</h1>
            <p className="mt-2 text-secondary-500">No writer at that address. <Link to="/tutorials" className="underline">Browse tutorials</Link>.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <BackLink to="/tutorials" label="Tutorials" />

        <div className="mt-6 flex items-center gap-4">
          <AuthorAvatar name={author.name} size={56} />
          <div>
            <h1 className="text-2xl font-bold">{author.name}</h1>
            <p className="text-secondary-400">{author.tagline}</p>
          </div>
        </div>

        <p className="mt-5 text-[1.05rem] leading-8 text-secondary-700 dark:text-secondary-300">{author.bio}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {author.expertise.map((e) => (
            <span key={e} className="rounded-full border border-secondary-200 dark:border-secondary-700 px-2.5 py-0.5 text-xs text-secondary-500">{e}</span>
          ))}
        </div>

        <h2 className="mt-10 text-sm font-semibold text-secondary-400 uppercase tracking-wide">Tutorials by {author.name.split(' ')[0]}</h2>
        <div className="mt-3 space-y-3">
          {tutorials.map((t) => (
            <Link key={t.slug} to={`/tutorials/${t.slug}`}
              className="group block rounded-xl border border-secondary-200 dark:border-secondary-800 p-4 hover:border-secondary-400 dark:hover:border-secondary-600 transition-colors">
              <h3 className="font-semibold text-secondary-900 dark:text-white group-hover:underline">{t.title}</h3>
              <p className="mt-1 text-sm text-secondary-500">{t.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuthorPage;
